import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ThemeConfig } from 'src/app/shared/models/theme-config';
import { ConstantUtil } from 'src/app/util/constant.util';

/**
 * 主題狀態
 */
export enum Status {
  Disable = 'Disable',
  Enable = 'Enable',
  New = 'New'
}

// Theme configuration interface (for preview)
// 统一使用共享的 ThemeConfig（canonical schema）

/**
 * Theme 模型
 */
export interface ThemeModel {
  /** 配置(JSON字串) */
  configJson?: string;
  /** 創建時間 */
  createTime?: number;
  /** 主題描述 */
  description?: string;
  id?: string;
  /** 主題名稱 */
  name?: string;
  /** 主題狀態 */
  status?: Status;
  /** 更新時間 */
  updateTime?: number;
  /** 是否为默认主题 */
  isDefault?: boolean;
}

@Component({
  selector: 'app-theme-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzTableModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    NzGridModule,
    NzPaginationModule,
    NzTagModule,
    NzToolTipModule,
    NzAffixModule,
    NzModalModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './theme-list.component.html',
  styleUrls: ['./theme-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThemeListComponent implements OnInit {
  private readonly http = inject(HttpService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly router = inject(Router);

  loading = false;
  pageSize = 20;
  pageIndex = 1;
  total = 0;
  searchKeywords = '';
  // 统一与其他列表的排序策略：使用 refreshTime + direction
  direction: 'asc' | 'desc' = 'desc';
  refreshTime = Date.now();
  listOfData: ThemeModel[] = [];
  // 选择模式：用于作为选择器嵌入其他页面
  @Input() selectMode: boolean = false;
  // 选择事件：在选择模式下，向父组件抛出选中的主题
  @Output() readonly themeSelected = new EventEmitter<ThemeModel>();
  // 預覽模態框狀態與配置
  previewVisible = false;
  previewConfig: ThemeConfig = this.getDefaultThemeConfig();
  // 當前預覽項在本頁的索引
  previewIndexOnPage: number = -1;
  // 當前預覽的主題資料
  previewItem: ThemeModel | null = null;
  // 采用路由页进行新增/编辑，移除模态框相关状态

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    const params = {
      pageIndex: this.pageIndex - 1,
      pageSize: this.pageSize,
      key: this.searchKeywords,
      direction: this.direction,
      refreshTime: this.refreshTime
    };

    this.http
      .post(ConstantUtil.BASE_THEME_V1_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          const rawItems = res?.data?.items || [];
          this.listOfData = rawItems.map((it: any) => ({
            ...it,
            isDefault: !!(it?.isDefault ?? it?.default)
          }));
          this.total = res?.data?.totalCount || 0;
        },
        error: () => {
          this.message.error(this.i18n.fanyi('common.load-failed'));
        }
      });
  }

  search(): void {
    this.pageIndex = 1;
    this.refreshTime = Date.now();
    this.loadData();
  }

  // 选择当前主题（仅在选择模式下使用）
  onSelect(item: ThemeModel): void {
    if (this.selectMode) {
      this.themeSelected.emit(item);
    }
  }

  reset(): void {
    this.searchKeywords = '';
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  onPageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.refreshTime = Date.now();
    this.pageIndex = 1;
    this.loadData();
  }

  // 狀態對應 i18n key
  getStatusI18nKey(status?: Status): string {
    switch (status) {
      case Status.Enable:
        return 'theme.status.enable';
      case Status.Disable:
        return 'theme.status.disable';
      case Status.New:
      default:
        return 'theme.status.new';
    }
  }

  // 狀態對應顏色
  getStatusColor(status?: Status): string {
    switch (status) {
      case Status.Enable:
        return 'green';
      case Status.Disable:
        return 'red';
      case Status.New:
        return 'blue';
      default:
        return 'default';
    }
  }

  // 打開預覽模態框
  openPreview(item: ThemeModel, index?: number): void {
    // 解析配置，合併默認值以確保完整性
    const cfgStr = (item as any)?.configJson || (item as any)?.config || (item as any)?.themeJson;
    const cfg = this.safeParseConfig(cfgStr);
    this.previewConfig = this.mergeThemeConfig(cfg);
    // 記錄索引，優先使用模板傳入的索引
    this.previewIndexOnPage = typeof index === 'number' ? index : this.listOfData.indexOf(item);
    this.previewItem = item ?? null;
    this.previewVisible = true;
  }

  closePreview(): void {
    this.previewVisible = false;
  }

  // 默認配置
  private getDefaultThemeConfig(): ThemeConfig {
    return {
      primaryColor: '#1890FF',
      backgroundColor: '#F5F7FA',
      headerTextColor: '#FFFFFF',
      customerBubbleBg: '#E6F7FF',
      customerBubbleText: '#1F2937',
      agentBubbleBg: '#FAFAFA',
      agentBubbleText: '#1F2937',
      headerBg: '#1890FF',
      footerBg: '#FFFFFF',
      bubbleRadius: '8px',
      windowRadius: '12px',
      boxShadow: { medium: '0 4px 12px rgba(0,0,0,0.15)' },
      fontSize: { base: '16px' }
    };
  }

  private mergeThemeConfig(partial: any): ThemeConfig {
    const def = this.getDefaultThemeConfig();
    const merged = {
      ...def,
      ...(partial || {}),
      boxShadow: { ...def.boxShadow, ...(partial?.boxShadow || {}) },
      fontSize: { ...def.fontSize, ...(partial?.fontSize || {}) }
    } as ThemeConfig;
    return merged;
  }

  private safeParseConfig(json?: string): any {
    if (!json) return {};
    try {
      const obj = JSON.parse(json);
      return typeof obj === 'object' && obj ? obj : {};
    } catch {
      return {};
    }
  }

  // 是否可前一個/下一個
  canPrev(): boolean {
    return this.previewVisible && (this.previewIndexOnPage > 0 || this.pageIndex > 1);
  }

  canNext(): boolean {
    const hasNextInPage = this.previewIndexOnPage < this.listOfData.length - 1;
    const hasNextPage = this.pageIndex * this.pageSize < this.total;
    return this.previewVisible && (hasNextInPage || hasNextPage);
  }

  // 上一個
  previewPrev(): void {
    if (!this.previewVisible) return;
    if (this.previewIndexOnPage > 0) {
      this.previewIndexOnPage -= 1;
      const item = this.listOfData[this.previewIndexOnPage];
      if (item) this.openPreview(item, this.previewIndexOnPage);
      return;
    }
    // 翻至上一頁最後一筆
    if (this.pageIndex > 1) {
      this.loadPageAndPreview(this.pageIndex - 1, 'last');
    }
  }

  // 下一個
  previewNext(): void {
    if (!this.previewVisible) return;
    const nextIndex = this.previewIndexOnPage + 1;
    if (nextIndex < this.listOfData.length) {
      this.previewIndexOnPage = nextIndex;
      const item = this.listOfData[this.previewIndexOnPage];
      if (item) this.openPreview(item, this.previewIndexOnPage);
      return;
    }
    // 翻至下一頁第一筆
    const hasNextPage = this.pageIndex * this.pageSize < this.total;
    if (hasNextPage) {
      this.loadPageAndPreview(this.pageIndex + 1, 0);
    }
  }

  // 載入指定頁，並開啟預覽（index 可為數字或 'last'）
  private loadPageAndPreview(targetPageIndex: number, targetIndex: number | 'last'): void {
    this.loading = true;
    const params = {
      pageIndex: targetPageIndex - 1,
      pageSize: this.pageSize,
      key: this.searchKeywords,
      direction: this.direction,
      refreshTime: this.refreshTime
    };

    this.http
      .post(ConstantUtil.BASE_THEME_V1_LIST, params)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (res: any) => {
          const items: ThemeModel[] = res?.data?.items || [];
          this.listOfData = items;
          this.total = res?.data?.totalCount || this.total;
          this.pageIndex = targetPageIndex;
          const index = targetIndex === 'last' ? Math.max(items.length - 1, 0) : Number(targetIndex);
          const item = items[index];
          if (item) {
            this.openPreview(item, index);
          } else {
            // 若無項目，關閉預覽
            this.previewVisible = false;
            this.previewItem = null;
          }
        },
        error: () => {
          this.message.error(this.i18n.fanyi('common.load-failed'));
        }
      });
  }

  // 是否显示启用/禁用按钮
  shouldShowEnableButton(item: ThemeModel): boolean {
    return item?.status !== Status.Enable;
  }

  shouldShowDisableButton(item: ThemeModel): boolean {
    return item?.status === Status.Enable;
  }

  // 是否显示“设为默认”按钮
  shouldShowSetDefaultButton(item: ThemeModel): boolean {
    return !!item?.id && !item?.isDefault;
  }

  // 路由跳转：新增
  goToCreate(): void {
    this.router.navigateByUrl('/themes/item');
  }

  // 路由跳转：编辑
  goToEdit(item: ThemeModel): void {
    const id = item?.id;
    if (!id) {
      return;
    }
    this.router.navigateByUrl(`/themes/item/${id}`);
  }

  // 启用主题
  enableTheme(item: ThemeModel): void {
    if (!item?.id) {
      return;
    }
    this.loading = true;
    this.http
      .post(`${ConstantUtil.BASE_THEME_V1_ENABLE}/${item.id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.i18n.fanyi('theme.list.message.enable-success'));
          this.loadData();
        } else {
          this.message.error(this.i18n.fanyi('theme.list.message.operation-failed'));
        }
      });
  }

  // 禁用主题
  disableTheme(item: ThemeModel): void {
    if (!item?.id) {
      return;
    }
    this.loading = true;
    this.http
      .post(`${ConstantUtil.BASE_THEME_V1_DISABLE}/${item.id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.i18n.fanyi('theme.list.message.disable-success'));
          this.loadData();
        } else {
          this.message.error(this.i18n.fanyi('theme.list.message.operation-failed'));
        }
      });
  }

  // 设为默认主题
  setDefaultTheme(item: ThemeModel): void {
    if (!item?.id) return;
    this.loading = true;
    this.http
      .post(`${ConstantUtil.BASE_THEME_V1_DEFAULT_SET}/${item.id}`, {})
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe(res => {
        if (res?.success === true && (res.data === true || res.data === undefined)) {
          this.message.success(this.i18n.fanyi('theme.item.set-default-success'));
          this.loadData();
        } else {
          this.message.error(this.i18n.fanyi('theme.list.message.operation-failed'));
        }
      });
  }

  // 删除主题（二次确认）
  deleteTheme(item: ThemeModel): void {
    if (!item?.id) {
      return;
    }
    this.modal.confirm({
      nzTitle: this.i18n.fanyi('theme.list.confirm.delete-title'),
      nzContent: this.i18n.fanyi('theme.list.confirm.delete-content'),
      nzOkDanger: true,
      nzOnOk: () => {
        this.loading = true;
        this.http
          .post(`${ConstantUtil.BASE_THEME_V1_DELETE}/${item.id}`, {})
          .pipe(
            finalize(() => {
              this.loading = false;
              this.cdr.detectChanges();
            })
          )
          .subscribe(res => {
            if (res?.success) {
              this.message.success(this.i18n.fanyi('theme.list.message.delete-success'));
              this.loadData();
            } else {
              this.message.error(res?.msg ?? this.i18n.fanyi('theme.list.message.delete-failed'));
            }
          });
      }
    });
  }
}
